import { useState, useEffect } from "react";
import R from "ramda";
import dayjs from "dayjs";

import overlay from "assets/images/share-overlay-logo.png";
import placeholder from "assets/images/placeholder-headcut.png";
import FacePresenter from "presenters/FacePresenter";
import TemplateGroupPresenter from "presenters/TemplateGroupPresenter";
import { resetCacheKey } from "utils/imageUtils";
import { jjLogger } from "utils/logUtils";
import localStorage from "utils/localStorage";
import { setCastingOrder } from "utils/castingUtils";
import useFaces from "hooks/useFaces";
import useWebGLRenderLibrary from "hooks/useWebGLRenderLibrary";
import { FACE_RELATIONS_TAGS } from "enums";

const CONSTANTS = {
  castingCardId: "castingCardID",
  canvasId: "casting-canvas",
};

const faceToCast = (face, role = null) => {
  const headID = FacePresenter.id(face);
  const imageURL = resetCacheKey(FacePresenter.asset(face));
  const toCast = {
    headID,
    imageURL,
    role,
  };

  if (R.isEmpty(headID) || R.isEmpty(imageURL)) {
    jjLogger.log(
      `AutoCast: useCasting.js - faceToCast - Error to get data from Face, Face: ${JSON.stringify(
        face,
      )} - toCast: ${JSON.stringify(toCast)}`,
    );
  }

  jjLogger.logDebug("AutoCast: useCasting.js - faceToCast - toCast with all data: ");
  jjLogger.logDebug(toCast);

  return toCast;
};

const getCastingParams = (url) => {
  return {
    feature: url,
    placeHolderImage: placeholder,
    target: CONSTANTS.canvasId,
    id: CONSTANTS.castingCardID,
    shareThumb: {
      width: 460,
      height: 240,
      overlayImage: overlay,
    },
  };
};

const castToRoles = (cast) => {
  const roles = {};
  cast.forEach((head) => {
    roles[head.role] = head;
  });
  return roles;
};

const setNewOrder = (order, castingCount) => {
  if (!order || R.isEmpty(order)) {
    return R.range(1, R.inc(castingCount)).reverse();
  }
  const newOrder = order.filter((x) => x <= castingCount);
  if (newOrder.length < castingCount) {
    for (let i = Math.max(...newOrder) + 1; i <= castingCount; i += 1) {
      newOrder.unshift(i);
    }
  }
  return newOrder;
};

export const deleteHeadFromCastingData = (headID) => {
  const castingData = localStorage.getItem("castingData");
  if (!castingData) return undefined;
  const { roles, order } = castingData;
  const deletedRoles = [];
  R.map((role) => (role.headID === headID ? deletedRoles.push(role.role) : role), roles);
  deletedRoles.forEach((role) => delete roles[role]);
  return localStorage.setItem("castingData", { roles, order });
};

const setCastingData = (card, order = null) => {
  if (R.isNil(card)) {
    jjLogger.log(`AutoCast: setCastingData - Card is Nil while trying to cast the data: ${JSON.stringify(card)}`);
    return;
  }

  if (R.isEmpty(card)) {
    jjLogger.log(`AutoCast: setCastingData - Card is Empty while trying to cast the data: ${JSON.stringify(card)}`);
    return;
  }

  const { actors } = card;
  const headsWithoutPlaceholders = R.values(actors).filter((actor) => actor.headID);
  const newRoles = castToRoles(headsWithoutPlaceholders);

  Object.entries(newRoles).forEach((newRole) => {
    if (R.isEmpty(newRole.headID) || R.isEmpty(newRole.imageURL)) {
      jjLogger.log(
        `AutoCast: setCastingData - Head id or imageURL not present on newRoles object: ${JSON.stringify(newRole)}`,
      );
    }
  });

  if (order) {
    localStorage.setItem("castingData", { roles: newRoles, order });
    return;
  }
  const currentCastingData = localStorage.getItem("castingData");
  if (!currentCastingData) {
    const newOrder = R.keys(newRoles)
      .sort()
      .reverse();
    const castingData = { order: newOrder, roles: newRoles };
    localStorage.setItem("castingData", castingData);
    return;
  }
  const { roles: currenrRoles, order: currentOrder } = currentCastingData;
  const changedRole = R.difference(R.keys(newRoles), R.keys(currenrRoles))
    .map((cast) => cast.role)
    .pop();
  const newOrder = R.includes(changedRole, currentOrder)
    ? currentOrder.sort((role) => {
        return role === changedRole ? -1 : 0;
      })
    : R.prepend(changedRole, currentOrder);

  const castingData = {
    order: changedRole ? newOrder : currentOrder,
    roles: newRoles,
  };
  localStorage.setItem("castingData", castingData);
};

const useCasting = () => {
  const [card, setCard] = useState(null);
  const [faces, setFace] = useState(null);
  const [peopleCalendarBirthday, setPeopleCalendarBirthday] = useState([]);
  const [people, setPeople] = useState([]);
  const [templateGroup, setTemplateGroup] = useState(null);
  const { faces: userFaces, addedFaces, resetAdded } = useFaces();
  const { load: loadRenderLib, renderLib } = useWebGLRenderLibrary();
  const [isSmartCastEnabled, setSmartCastEnabled] = useState(false);
  const castingCount = TemplateGroupPresenter.castsCount(templateGroup);
  const isAOneRoleTemplate = castingCount === 1;
  const [roleIndex, setRoleIndex] = useState(null);

  useEffect(() => {
    loadRenderLib();
  }, []);

  useEffect(() => {
    if (R.isNil(renderLib) || R.isNil(templateGroup) || R.isEmpty(templateGroup)) return;

    if (window.castingCard) {
      setRoleIndex(window.castingCard.roleIndex < window.castingCard.roles - 1 ? window.castingCard.roleIndex + 1 : 0);
    }
    const { CastingCard } = renderLib;
    const parameters = getCastingParams(TemplateGroupPresenter.castingCardThumb(templateGroup));
    const castingCard = new CastingCard(parameters);
    window.castingCard = castingCard;
    castingCard.init().then(() => {
      setCard(castingCard);
    });
  }, [renderLib, templateGroup]);

  const init = (face, tg, peopleCalendarBday, isSCastEnabled, peopleRelated) => {
    setFace(face);
    setTemplateGroup(tg);
    setPeopleCalendarBirthday(peopleCalendarBday);
    setSmartCastEnabled(isSCastEnabled);
    setPeople(peopleRelated);
  };

  const cleanup = () => {
    if (card) {
      card.destroy();
      setCard(null);
    }
  };

  const castFace = (face, role = null) => {
    const cast = faceToCast(face, role);

    jjLogger.logDebug("AutoCast: useCasting.js - castFace: ");
    jjLogger.logDebug(cast);

    if (R.isEmpty(card)) {
      jjLogger.log(`AutoCast: useCasting.js - castFace - Casting card Empty: ${JSON.stringify(card)}`);
    }

    if (R.isNil(card)) {
      jjLogger.log(`AutoCast: useCasting.js - castFace - Casting card Nil: ${JSON.stringify(card)}`);
    }

    return card
      .addCastMember(cast)
      .then((actor) => {
        if (!actor.headID || !actor.imageURL) {
          jjLogger.log(
            `AutoCast: useCasting.js - castFace - Missing head from Casting Carg - actor : ${JSON.stringify(actor)}`,
          );
        }
        card.update();
        setCastingOrder();
        return setCastingData(card);
      })
      .catch((error) => jjLogger.log(`AutoCast: useCasting.js - castFace - card.addCastMember error - ${error}`));
  };

  const shouldCastPeopleCalendarBirthday = () => {
    const peopleBirthday = peopleCalendarBirthday?.[0];
    const hoursInMonth = 720;
    const january = 0;

    if (!isSmartCastEnabled || !TemplateGroupPresenter.hasTag(templateGroup, "birthday") || R.isNil(peopleBirthday))
      return false;

    const today = dayjs();
    let birthday = dayjs(peopleBirthday.person.birthday);
    const year = birthday.month() === january && today.month() !== january ? today.year() + 1 : today.year();

    birthday = dayjs(`${year}-${birthday.format("MM-DD")}`);

    return birthday.diff(today, "hour") <= hoursInMonth;
  };

  const getRelationTemplateTag = () => {
    return Object.keys(FACE_RELATIONS_TAGS).find((relation) => TemplateGroupPresenter.hasTag(templateGroup, relation));
  };

  const autoCast = () => {
    const castingData = localStorage.getItem("castingData");
    const facesWithJaws = faces.filter(FacePresenter.jaw);

    const relationTag = getRelationTemplateTag();
    if (relationTag && !R.isEmpty(people) && R.isEmpty(addedFaces) && isAOneRoleTemplate) {
      const relatedPerson = people.find((person) => person.relation === FACE_RELATIONS_TAGS[relationTag]);
      if (relatedPerson) return castFace(relatedPerson.defaultHead);
    }

    if (shouldCastPeopleCalendarBirthday() && R.isEmpty(addedFaces)) {
      return castFace(peopleCalendarBirthday[0].person.defaultHead);
    }

    if (!castingData) {
      jjLogger.logDebug("AutoCast: useCasting.js - autocast() - no casting data");
      const castings = facesWithJaws.slice(0, castingCount).map((face, index) => faceToCast(face, R.inc(index)));
      resetAdded();

      jjLogger.logDebug("AutoCast: useCasting.js - autocast() - add casting data ");
      jjLogger.logDebug(castings);

      return card
        .addCast(castings)
        .then(() => {
          card.update();
          setCastingData(card);
        })
        .catch((error) => {
          jjLogger.log(`AutoCast: useCasting.js - autocast() - Empty casting object passed to CastingCard - ${error}`);
        });
    }

    const { roles, order } = castingData;
    const newOrder = setNewOrder(order, castingCount);
    if (!R.isEmpty(addedFaces)) {
      jjLogger.logDebug("AutoCast: useCasting.js - autocast() - Added faces not empty");
      const currentEmptyRoles = newOrder.filter(
        (el) => !R.values(roles).find((head) => Number(head.role) === Number(el)),
      );
      const castingAddedFaces = addedFaces.map((face) => {
        const role = R.isEmpty(currentEmptyRoles) ? R.last(newOrder) : currentEmptyRoles.pop();
        jjLogger.logDebug("AutoCast: useCasting.js - autocast() - casting added faces to empty role:");
        jjLogger.logDebug(role);
        newOrder.forEach((item, i) => {
          if (item === role) {
            newOrder.splice(i, 1);
            newOrder.unshift(item);
          }
        });

        jjLogger.logDebug("AutoCast: useCasting.js - autocast() - face to cast:");
        jjLogger.logDebug(face);
        const toCast = faceToCast(face, Number(role));
        jjLogger.logDebug("AutoCast: useCasting.js - autocast() - toCast:");
        jjLogger.logDebug(toCast);

        return toCast;
      });
      const addedRoles = castToRoles(castingAddedFaces);

      const emptyRoles = newOrder.filter(
        (el) => !R.concat(castingAddedFaces, R.values(roles)).find((head) => Number(head.role) === Number(el)),
      );
      const freeFaces = facesWithJaws.filter(
        (el) => !R.concat(castingAddedFaces, R.values(roles)).find((head) => head.headID === el.id),
      );
      const castingFillerHeads = freeFaces.slice(0, emptyRoles.length).map((face) => {
        const role = emptyRoles.pop();
        return faceToCast(face, role);
      });
      const rolesFillers = castToRoles(castingFillerHeads);

      const updatedRoles = { ...roles, ...addedRoles, ...rolesFillers };

      resetAdded();
      const toCastSlice = R.slice(0, castingCount, R.values(updatedRoles));
      jjLogger.logDebug("AutoCast: useCasting.js - autocast() - add cast no castingData:");
      jjLogger.logDebug(toCastSlice);

      return card.addCast(toCastSlice).then((actors) => {
        actors.forEach((actor) => {
          if (!actor.headID || !actor.imageURL) {
            jjLogger.log(
              `AutoCast: useCasting.js - card.addCast - Missing head from castingCarg.addCast - actor : ${JSON.stringify(
                actor,
              )}`,
            );

            const uncastedFaces = userFaces.filter(
              (face) =>
                !actors
                  .filter((actorToCheck) => {
                    return typeof actorToCheck.headID !== "undefined";
                  })
                  .map((actorToCheck) => actorToCheck.headID)
                  .includes(face.id),
            );

            if (!R.isEmpty(uncastedFaces)) {
              castFace(uncastedFaces[0], actor.role);
              jjLogger.log(
                `AutoCast: useCasting.js - card.addCast - face ${JSON.stringify(
                  uncastedFaces[0],
                )} added to missing role: ${actor.role}`,
              );
            }
          }
        });

        if (!R.isNil(roleIndex)) {
          card.roleIndex = roleIndex;
          card.update();
          setCastingOrder();
        }
        return setCastingData(card, newOrder);
      });
    }

    const emptyRoles = newOrder.filter((el) => !R.values(roles).find((head) => Number(head.role) === Number(el)));
    const freeFaces = facesWithJaws.filter((el) => !R.values(roles).find((head) => head.headID === el.id));
    const castingFillerHeads = freeFaces.slice(0, emptyRoles.length).map((face) => {
      const role = emptyRoles.pop();
      const toCast = faceToCast(face, Number(role));

      jjLogger.logDebug("useCasting.js - autocast() - delete face data to cast - original toCast:");
      jjLogger.logDebug(toCast);

      return toCast;
    });
    const rolesFillers = castToRoles(castingFillerHeads);
    const updatedRoles = { ...roles, ...rolesFillers };
    const toCastSliceUpdateRoles = R.slice(0, castingCount, R.values(updatedRoles));
    jjLogger.logDebug("AutoCast: useCasting.js - autocast() - toCastSliceUpdateRoles:");
    jjLogger.logDebug(toCastSliceUpdateRoles);
    return card
      .addCast(toCastSliceUpdateRoles)
      .then((actors) => {
        actors.forEach((actor) => {
          if (!actor.headID || !actor.imageURL) {
            jjLogger.log(
              `AutoCast: useCasting.js - card.addCast - Missing head from castingCarg.addCast - actor : ${JSON.stringify(
                actor,
              )}`,
            );

            const uncastedFaces = userFaces.filter(
              (face) =>
                !actors
                  .filter((actorToCheck) => {
                    return typeof actorToCheck.headID !== "undefined";
                  })
                  .map((actorToCheck) => actorToCheck.headID)
                  .includes(face.id),
            );

            if (!R.isEmpty(uncastedFaces)) {
              castFace(uncastedFaces[0], actor.role);
              jjLogger.log(
                `AutoCast: useCasting.js - card.addCast - face ${JSON.stringify(
                  uncastedFaces[0],
                )} added to missing role: ${actor.role}`,
              );
            }
          }
        });

        card.update();
        setCastingOrder();
        return setCastingData(card);
      })
      .catch((error) => jjLogger.log(error));
  };

  const getRoles = () => {
    if (!card) return null;

    const roles = R.values(card.actors).map(({ role, headID }) => ({ roleRank: role, headID }));

    roles.forEach((role, index) => {
      if (!role.headID) {
        jjLogger.logDebug("AutoCast: useCasting.js - getRoles() - role without headID - role:");
        jjLogger.logDebug(role);
        const uncastedFaces = userFaces.filter(
          (face) =>
            !roles
              .filter((roleToCheck) => {
                return typeof roleToCheck.headID !== "undefined";
              })
              .map((roleToCheck) => roleToCheck.headID)
              .includes(face.id),
        );

        if (!R.isEmpty(uncastedFaces)) {
          roles[index] = { roleRank: role.roleRank, headID: uncastedFaces[0].id };
          jjLogger.logDebug("AutoCast: useCasting.js - getRoles() - oles fixed - role:");
          jjLogger.logDebug(roles[index]);
        }
      }
    });

    return roles;
  };

  const getData = () => {
    return {
      roles: getRoles(),
      makeThumbUrl: card.getThumbnail(),
      shareThumbUrl: card.getSharedThumbnail(),
      templateId: TemplateGroupPresenter.template(templateGroup).id,
    };
  };

  return { CONSTANTS, card, autoCast, init, cleanup, castFace, getData, castingCount };
};

export default useCasting;
